import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { AdminDelegateDocumentRequest, DocumentsService } from 'api';
import { MAX_DELEGATION_DEPTH } from 'common/constants/delegation';
import { DocumentProgress, QuestionProgress } from '../types';
import { AdminDelegateModalProps, DelegationStatusResponse } from './AdminDelegateModal.props';
import { getNotificationLabels, isValidEmail } from '../../util/delegationValidation';
import { filterDelegatableDocuments, filterDelegatableQuestions } from '../../util/filterInformationRequests';

const AdminDelegateModal = ({
  open,
  onClose,
  onSuccess,
  taskId,
  requestedFiles,
  requestedQuestions = [],
  skipQuestionnaire = false,
  delegationDepth = 0,
}: AdminDelegateModalProps) => {
  const [formData, setFormData] = useState({
    delegatee_name: '',
    delegatee_email: '',
    notification_preference: AdminDelegateDocumentRequest.notification_preference.EACH_UPLOAD,
  });
  const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<number[]>([]);
  const [delegateAllFiles, setDelegateAllFiles] = useState(true);
  const [delegateAllQuestions, setDelegateAllQuestions] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [delegationStatus, setDelegationStatus] = useState<DelegationStatusResponse | null>(null);
  const [isDelegationStatusLoading, setIsDelegationStatusLoading] = useState(false);

  // Fetch delegation status when modal opens
  useEffect(() => {
    if (open && taskId) {
      setIsDelegationStatusLoading(true);
      // Note: The generated type expects pagination, but backend returns { has_delegations, delegations }
      DocumentsService.apiDocumentsRequestsTaskAppDelegateList(String(taskId))
        .then(response => {
          // Cast to actual response type since swagger docs are incorrect
          const actualResponse = response as unknown as DelegationStatusResponse;
          setDelegationStatus(actualResponse);
        })
        .catch(() => setDelegationStatus(null))
        .finally(() => setIsDelegationStatusLoading(false));
    }
  }, [open, taskId]);

  // Filter out files that are already uploaded and questions that are already answered
  const delegatableFiles = useMemo(
    () => filterDelegatableDocuments(requestedFiles || []),
    [requestedFiles]
  );
  const delegatableQuestions = useMemo(
    () => (skipQuestionnaire ? [] : filterDelegatableQuestions(requestedQuestions || [])),
    [skipQuestionnaire, requestedQuestions]
  );

  const hasFiles = delegatableFiles.length > 0;
  const hasQuestions = !skipQuestionnaire && delegatableQuestions.length > 0;
  const hasBoth = hasFiles && hasQuestions;

  // Check if delegation depth exceeds maximum
  const canDelegate = delegationDepth < MAX_DELEGATION_DEPTH;
  const isMaxDepthReached = !canDelegate;

  // Determine if the submit button should be disabled
  const willDelegateFiles = hasFiles && (delegateAllFiles || selectedFileIds.length > 0);
  const willDelegateQuestions = hasQuestions && (delegateAllQuestions || selectedQuestionIds.length > 0);
  const hasNothingToDelegate = !willDelegateFiles && !willDelegateQuestions;

  const handleChange = useCallback(
    (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData(prev => ({
        ...prev,
        [field]: e.target.value,
      }));
      setErrors(prev => (prev[field] ? { ...prev, [field]: '' } : prev));
    },
    []
  );

  const handleFileToggle = useCallback((fileId: number) => {
    setSelectedFileIds(prev =>
      prev.includes(fileId) ? prev.filter(id => id !== fileId) : [...prev, fileId]
    );
  }, []);

  const handleQuestionToggle = useCallback((questionId: number) => {
    setSelectedQuestionIds(prev =>
      prev.includes(questionId) ? prev.filter(id => id !== questionId) : [...prev, questionId]
    );
  }, []);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.delegatee_name.trim()) {
      newErrors.delegatee_name = 'Name is required';
    }

    if (!formData.delegatee_email.trim()) {
      newErrors.delegatee_email = 'Email is required';
    } else if (!isValidEmail(formData.delegatee_email)) {
      newErrors.delegatee_email = 'Invalid email format';
    }

    // Note: Users can choose to skip documents or questions entirely.
    // If they uncheck "delegate all" and select nothing, that category is skipped.
    // We only validate that at least ONE category has something to delegate.

    // Must have at least something to delegate
    const willDelegateFiles = hasFiles && (delegateAllFiles || selectedFileIds.length > 0);
    const willDelegateQuestions = hasQuestions && (delegateAllQuestions || selectedQuestionIds.length > 0);

    if (!willDelegateFiles && !willDelegateQuestions) {
      newErrors.general = 'Nothing to delegate. Please select at least one document or question.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const resetForm = useCallback(() => {
    setFormData({
      delegatee_name: '',
      delegatee_email: '',
      notification_preference: AdminDelegateDocumentRequest.notification_preference.EACH_UPLOAD,
    });
    setSelectedFileIds([]);
    setSelectedQuestionIds([]);
    setDelegateAllFiles(true);
    setDelegateAllQuestions(true);
    setErrors({});
    setDelegationStatus(null);
  }, []);

  const handleSubmit = async () => {
    if (!validate() || !taskId) {
      return;
    }

    const payload: AdminDelegateDocumentRequest & { skip_questionnaire?: boolean; skip_documents?: boolean } = {
      delegatee_name: formData.delegatee_name.trim(),
      delegatee_email: formData.delegatee_email.trim(),
      notification_preference: formData.notification_preference,
    };

    // Determine if user is skipping documents (unchecked delegate all AND no files selected)
    const userSkipsDocuments = hasFiles && !delegateAllFiles && selectedFileIds.length === 0;

    // Add file IDs if partial file delegation
    if (!delegateAllFiles && selectedFileIds.length > 0) {
      payload.requested_file_ids = selectedFileIds;
    }

    // Skip document delegation if user opted out
    if (userSkipsDocuments) {
      payload.skip_documents = true;
    }

    // Determine if user is skipping questions (unchecked delegate all AND no questions selected)
    const userSkipsQuestions = hasQuestions && !delegateAllQuestions && selectedQuestionIds.length === 0;

    // Add question IDs if partial question delegation
    if (!skipQuestionnaire && !userSkipsQuestions && !delegateAllQuestions && selectedQuestionIds.length > 0) {
      payload.question_ids = selectedQuestionIds;
    }

    // Skip questionnaire delegation if requested via prop OR user opted out
    if (skipQuestionnaire || userSkipsQuestions) {
      payload.skip_questionnaire = true;
    }

    setIsLoading(true);
    try {
      await DocumentsService.apiDocumentsRequestsTaskAppDelegateCreate(String(taskId), payload as AdminDelegateDocumentRequest);
      enqueueSnackbar('Request delegated successfully', { variant: 'success' });
      resetForm();
      onSuccess();
    } catch (error) {
      enqueueSnackbar('Failed to delegate request', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };
  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  // Determine notification label based on what's being delegated
  const notificationLabels = useMemo(
    () => getNotificationLabels(hasFiles, hasQuestions),
    [hasFiles, hasQuestions]
  );

  const renderDelegationStatusBanner = () => {
    if (isDelegationStatusLoading) {
      return (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Checking delegation status...
          </Typography>
        </Box>
      );
    }

    if (delegationStatus?.has_delegations) {
      return (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
          <Typography variant="body2" color="warning.dark" fontWeight="medium" sx={{ mb: 1 }}>
            This request is currently delegated:
          </Typography>
          {delegationStatus.delegations.map(delegation => (
            <Box key={delegation.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body2" color="warning.dark">
                {delegation.delegatee_name} ({delegation.delegatee_email})
              </Typography>
              <Chip
                size="small"
                label={delegation.is_complete ? 'Complete' : 'In Progress'}
                color={delegation.is_complete ? 'success' : 'warning'}
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            </Box>
          ))}
          <Typography variant="body2" color="warning.dark" sx={{ mt: 1, fontStyle: 'italic' }}>
            Creating a new delegation will invalidate the existing delegation(s) above.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ mb: 2, p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
        <Typography variant="body2" color="info.dark">
          This will create a new delegation. Any existing delegations will be invalidated.
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delegate Request</DialogTitle>
      <DialogContent>
        {renderDelegationStatusBanner()}

        {isMaxDepthReached && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Maximum delegation depth of {MAX_DELEGATION_DEPTH} has been reached. This request cannot be delegated further.
          </Alert>
        )}

        {errors.general && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'error.light', borderRadius: 1 }}>
            <Typography variant="body2" color="error.dark">
              {errors.general}
            </Typography>
          </Box>
        )}

        <TextField
          label="Recipient Name"
          fullWidth
          required
          value={formData.delegatee_name}
          onChange={handleChange('delegatee_name')}
          error={!!errors.delegatee_name}
          helperText={errors.delegatee_name}
          margin="normal"
          autoFocus
        />

        <TextField
          label="Recipient Email"
          fullWidth
          required
          type="email"
          value={formData.delegatee_email}
          onChange={handleChange('delegatee_email')}
          error={!!errors.delegatee_email}
          helperText={errors.delegatee_email}
          margin="normal"
        />

        {/* File Selection for Partial Delegation */}
        {hasFiles && (
          <Box sx={{ mt: 2 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Documents to delegate</FormLabel>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={delegateAllFiles}
                    onChange={e => setDelegateAllFiles(e.target.checked)}
                  />
                }
                label={`Delegate all documents (${delegatableFiles.length})`}
              />
            </FormControl>

            {!delegateAllFiles && (
              <Box sx={{ ml: 2, mt: 1, maxHeight: 150, overflow: 'auto' }}>
                {delegatableFiles.map((file: DocumentProgress) => (
                  <FormControlLabel
                    key={file.id}
                    control={
                      <Checkbox
                        checked={selectedFileIds.includes(file.id)}
                        onChange={() => handleFileToggle(file.id)}
                        size="small"
                      />
                    }
                    label={file.name}
                    sx={{ display: 'block' }}
                  />
                ))}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Leave all unchecked to skip document delegation
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Question Selection for Partial Delegation */}
        {hasQuestions && (
          <Box sx={{ mt: 2 }}>
            {hasFiles && <Divider sx={{ my: 2 }} />}
            <FormControl component="fieldset">
              <FormLabel component="legend">Questions to delegate</FormLabel>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={delegateAllQuestions}
                    onChange={e => setDelegateAllQuestions(e.target.checked)}
                  />
                }
                label={`Delegate all questions (${delegatableQuestions.length})`}
              />
            </FormControl>

            {!delegateAllQuestions && (
              <Box sx={{ ml: 2, mt: 1, maxHeight: 150, overflow: 'auto' }}>
                {delegatableQuestions.map((question: QuestionProgress) => (
                  <FormControlLabel
                    key={question.id}
                    control={
                      <Checkbox
                        checked={selectedQuestionIds.includes(question.id)}
                        onChange={() => handleQuestionToggle(question.id)}
                        size="small"
                      />
                    }
                    label={question.name}
                    sx={{ display: 'block' }}
                  />
                ))}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Leave all unchecked to skip question delegation
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Notification Preferences */}
        <Box sx={{ mt: 2 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend">Notification preference</FormLabel>
            <RadioGroup
              value={formData.notification_preference}
              onChange={handleChange('notification_preference')}
            >
              <FormControlLabel
                value={AdminDelegateDocumentRequest.notification_preference.EACH_UPLOAD}
                control={<Radio size="small" />}
                label={notificationLabels.each}
              />
              <FormControlLabel
                value={AdminDelegateDocumentRequest.notification_preference.ALL_COMPLETE}
                control={<Radio size="small" />}
                label={notificationLabels.all}
              />
              <FormControlLabel
                value={AdminDelegateDocumentRequest.notification_preference.NONE}
                control={<Radio size="small" />}
                label={notificationLabels.none}
              />
            </RadioGroup>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Notifications will be sent to the person originally assigned this request.
            </Typography>
          </FormControl>
        </Box>

        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="caption" color="info.dark">
            <strong>Note:</strong> The delegatee will only see the delegated items in their portal.
            Other documents or questions from this request will not be visible to them.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isLoading || hasNothingToDelegate || isMaxDepthReached}>
          {isLoading ? 'Sending...' : 'Send Delegation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminDelegateModal;
